import { useClickOutside } from '@lib/hooks'
import {
  type ExternalLink as ExternalLinkType,
  ProjectActions,
} from '@octopus-do/octopus-project-core'
import { thunkAddArrow } from '@store/migrationActions/thunkActions/thunkArrowsActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { commitProjectRequest } from '@storeOld/actionCreators'
import {
  CanvasActionTypes,
  setPreviewColor,
} from '@storeOld/reducers/canvas/canvas'
import cx from 'classnames'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import Draggable from 'react-draggable'
import { useDispatch, useSelector } from 'react-redux'

import { addProtocol } from '@/helpers'
import { useArtboardMode } from '@/hooks'
import { importSeoTags } from '@/services/importSeoTags'
import type { AppState } from '@/types'
import { isInsidePopover } from '@/utils'
import { ExternalLinkActionsToolbar } from './components/ExternalLinkActionsToolbar'
import { ExternalLinkLoading } from './components/ExternalLinkLoading'
import { Image } from './components/Image'
import { URL } from './components/URL'
import css from './ExternalLink.module.scss'
import useDraggable from './hooks/useDraggable'

type Props = {
  link: ExternalLinkType
}

interface ComponentProp extends Props {
  onPointerDown: (e: React.PointerEvent) => void
  onRedirect: () => void
  arrowMode: boolean
}

const ExternalLinkComponent: React.FC<ComponentProp> = ({
  onPointerDown,
  link,
  onRedirect,
  arrowMode,
}) => {
  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation()
  }

  const handleRedirect = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    onRedirect()
  }

  return (
    <div onPointerDown={onPointerDown}>
      <Image id={link.id} image={link.data.image} title={link.data.title} />

      <div
        className={cx(css.body, {
          [css.arrowMode]: arrowMode,
        })}
      >
        {link.data.title && (
          <div
            className={css.title}
            onMouseDown={stopPropagation}
            onMouseUp={stopPropagation}
            onClick={handleRedirect}
          >
            <div>{link.data.title}</div>
          </div>
        )}

        <URL url={link.data.url} onRedirect={onRedirect} />
      </div>
    </div>
  )
}

const ExternalLink: React.FC<Props> = ({ link }) => {
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()

  const nodeRef = useRef<HTMLDivElement>(null)

  const [canRedirect, setCanRedirect] = useState(true)

  const isSelected = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.externalLink.selectedExternalLink === link.id
  )

  const { arrows: isArrowMode } = useArtboardMode()

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const colorPickerIsOpen = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.colorPickerIsOpen
  )

  const { isDragging, ...draggableProps } = useDraggable(link)

  const projectId = useSelector(migrationProjectSelector.getProjectId)

  const setNoImage = () => {
    dispatch(
      ProjectActions.externalLinks.updateData({
        id: link.id,
        data: { image: null },
      })
    )

    dispatch(
      ProjectActions.externalLinks.changeLoading({
        id: link.id,
        loading: false,
      })
    )
  }

  useEffect(() => {
    if (!projectId) {
      // If it's an unsaved project, we don't save an image
      setNoImage()
      return
    }

    if (link.data.image === null) {
      setIsLoading(true)

      importSeoTags(projectId, link.data.url)
        .then((data) => {
          const image = data.image ? data.image.downloadUrl : null

          dispatch(
            commitProjectRequest(
              ProjectActions.externalLinks.updateData({
                id: link.id,
                data: {
                  image,
                  title: link.data.title || data.title,
                  url: link.data.url || data.url,
                },
              })
            )
          )

          dispatch(
            ProjectActions.externalLinks.changeLoading({
              id: link.id,
              loading: false,
            })
          )
        })
        .catch(() => {
          setNoImage()
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [projectId])

  const clearSelection = (target: EventTarget | null = null) => {
    if (!isSelected || colorPickerIsOpen || isInsidePopover(target)) {
      return
    }

    dispatch({
      type: CanvasActionTypes.CLEAR_SELECTED_EXTERNAL_LINK,
    })

    // @todo implement action
    // @ts-expect-error
    dispatch(setPreviewColor(null))
  }

  const onSelect = () => {
    if (isDragging || isReadOnly || isSelected) {
      return
    }

    dispatch({
      type: CanvasActionTypes.CLEAR_ALL_SELECTION,
    })

    dispatch({
      type: CanvasActionTypes.SELECT_EXTERNAL_LINK,
      payload: link.id,
    })
  }

  const onPointerDown = (e: React.PointerEvent) => {
    e.stopPropagation()

    if (!isArrowMode) {
      return
    }

    // @todo implement actions
    // @ts-expect-error
    dispatch(thunkAddArrow(link.id, 'external-link'))
  }

  const onRedirect = () => {
    if (!canRedirect) {
      return
    }

    window.open(addProtocol(link.data.url), '_blank')
  }

  useEffect(() => {
    if (isDragging) {
      setCanRedirect(false)
      clearSelection()
    }

    if (!isDragging) {
      setTimeout(() => {
        setCanRedirect(true)
      })
    }
  }, [isDragging])

  const clickOutsideProps = useClickOutside<HTMLDivElement>(isSelected, (e) =>
    clearSelection(e.target)
  )

  const classes = {
    container: cx(css.container, 'ExternalLink-container', {
      [css.selected]: isSelected,
    }),
  }

  const top = isReadOnly ? link.offset?.y : undefined
  const left = isReadOnly ? link.offset?.x : undefined

  const styles = {
    top,
    left,
  } as React.CSSProperties

  if (isReadOnly) {
    return (
      <div className={classes.container} style={styles} id={link.id}>
        <ExternalLinkComponent
          link={link}
          onPointerDown={onPointerDown}
          onRedirect={onRedirect}
          arrowMode={isArrowMode}
        />
      </div>
    )
  }

  return (
    <div {...clickOutsideProps} onMouseUp={onSelect}>
      {/* @ts-ignore */}
      <Draggable nodeRef={nodeRef} {...draggableProps}>
        <div
          ref={nodeRef}
          className={classes.container}
          style={styles}
          id={link.id}
        >
          {isSelected && !isDragging && (
            <ExternalLinkActionsToolbar link={link} />
          )}

          {isLoading ? (
            <ExternalLinkLoading
              title={link.data.title}
              url={link.data.url}
              isArrowMode={isArrowMode}
              onRedirect={onRedirect}
            />
          ) : (
            <ExternalLinkComponent
              link={link}
              onPointerDown={onPointerDown}
              onRedirect={onRedirect}
              arrowMode={isArrowMode}
            />
          )}
        </div>
      </Draggable>
    </div>
  )
}

export default ExternalLink
